import Button, { ButtonProps } from '@carvana/ds-button';
import { AnalyticsAttributes } from '@carvana/merch-components/contracts';
import React from 'react';
import SecondaryCtaType from '~/constants/SecondaryCtaType';
import { useConfiguration } from '~/context/common/StaticContext';
import { useSecondaryButtonHandler } from '~/hooks/vdp/cta';
import useSecondaryCtaButtonState from '~/hooks/vdp/selectors/useSecondaryCtaButtonState';
import { useGetCtaWrapperContent } from '../CtaWrapper';

const dummyFunction = () => {};

type OwnProps<T> = {
  ButtonComponent?: React.ComponentType<any>;
  className?: string;
  'data-analytics-id': string | null;
  'data-analytics-attributes'?: AnalyticsAttributes;
  'data-testid'?: string;
  onClick?: () => void;
} & T;

export default function SecondaryCtaButton<T = ButtonProps>({
  ButtonComponent = Button,
  className = '',
  'data-analytics-id': analyticsId,
  'data-analytics-attributes': analyticsAttributes,
  'data-testid': testId,
  onClick = dummyFunction,
  ...rest
}: OwnProps<T>) {
  const { shouldUseTitleCasing } = useConfiguration();
  const handleOnClick = useSecondaryButtonHandler(onClick) ?? dummyFunction;
  const { type } = useSecondaryCtaButtonState();
  const { showCtaFooter } = useGetCtaWrapperContent();

  const secondaryCtaTypes: Record<SecondaryCtaType, string | null> = {
    [SecondaryCtaType.Default]: null,
    [SecondaryCtaType.GetAlerts]: shouldUseTitleCasing
      ? 'Get Alerts'
      : 'Get alerts',
    [SecondaryCtaType.NotifyMe]: shouldUseTitleCasing
      ? 'Notify Me'
      : 'Notify me',
    [SecondaryCtaType.ViewMoreCars]: shouldUseTitleCasing
      ? 'View More Cars'
      : 'View more cars',
    [SecondaryCtaType.ViewSimilarVehicles]: shouldUseTitleCasing
      ? 'View Similar Vehicles'
      : 'View similar vehicles'
  };

  return secondaryCtaTypes[type] ? (
    <ButtonComponent
      className={className}
      onClick={handleOnClick}
      data-adobe-id={null}
      data-analytics-id={analyticsId}
      data-analytics-attributes={JSON.stringify({
        ...analyticsAttributes,
        description: 'secondary cta',
        text: secondaryCtaTypes[type]
      })}
      data-testid={testId}
      variant={showCtaFooter ? 'primaryInverted' : 'secondary'}
      {...rest}
    >
      {secondaryCtaTypes[type]}
    </ButtonComponent>
  ) : null;
}
